// Alerts.jsx
import React, { useState } from "react";
import SuperAdminContainer from "../SuperAdminContainer";
import { ChantButton, ScreenHeader } from "../../../CommonComponents";
import { useNavigate } from "react-router-dom";
import { Table, Modal, Typography, Tag, Switch } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import dayjs from "dayjs";
import { db } from "../../../services/firebaseService/connection";
import { PictureOutlined, LinkOutlined, EditOutlined } from "@ant-design/icons";

const { Link } = Typography;

const Alerts = () => {
  const navigate = useNavigate();
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const {
    data: alerts = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["adminAlerts"],
    queryFn: async () => {
      const alertsRef = collection(db, "adminAlerts");
      const q = query(alertsRef, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },
  });

  const handleViewDescription = (alert) => {
    setSelectedAlert(alert);
    setIsModalOpen(true);
  };

  const handleToggleActive = async (checked, alert) => {
    setTogglingId(alert.id);
    try {
      await updateDoc(doc(db, "adminAlerts", alert.id), {
        isActive: checked,
      });
      refetch();
    } catch (error) {
      console.error("Error toggling alert:", error);
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      title: "Actions",
      key: "edit",
      width: 100,
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <EditOutlined
            onClick={() => navigate(`/superadmin/alerts/edit/${record.id}`)}
            style={{ fontSize: 18, color: "#718096", cursor: "pointer" }}
          />
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Description",
      key: "description",
      render: (_, record) => (
        <Link onClick={() => handleViewDescription(record)}>View</Link>
      ),
    },
    {
      title: "",
      key: "extras",
      width: 60,
      render: (_, record) => (
        <span style={{ display: "flex", gap: 8 }}>
          {record.imageUrl && <PictureOutlined style={{ color: "#718096" }} />}
          {record.ctaUrl && <LinkOutlined style={{ color: "#718096" }} />}
        </span>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority) => {
        const colors = { 1: "red", 2: "orange", 3: "green" };
        const labels = { 1: "High", 2: "Medium", 3: "Low" };
        return (
          <Tag color={colors[priority]}>{labels[priority] || priority}</Tag>
        );
      },
    },
    {
      title: "Target",
      dataIndex: "targetType",
      key: "targetType",
      render: (targetType) => (
        <Tag color={targetType === "global" ? "blue" : "purple"}>
          {targetType === "global" ? "🌐 Global" : "🎯 Specific"}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (timestamp) =>
        timestamp ? dayjs(timestamp).format("MMM D, YYYY") : "-",
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      render: (timestamp) => {
        if (!timestamp) return <span style={{ color: "#a0aec0" }}>Never</span>;
        const isExpired = timestamp < Date.now();
        return (
          <span style={{ color: isExpired ? "#e53e3e" : "#718096" }}>
            {dayjs(timestamp).format("MMM D, YYYY")}
            {isExpired && " (Expired)"}
          </span>
        );
      },
    },
    {
      title: "Active",
      key: "isActive",
      render: (_, record) => (
        <Switch
          checked={record.isActive}
          size="small"
          loading={togglingId === record.id}
          disabled={!!togglingId}
          onChange={(checked) => handleToggleActive(checked, record)}
        />
      ),
    },
  ];

  return (
    <SuperAdminContainer>
      <ScreenHeader
        title="Alerts"
        actions={
          <ChantButton
            type="primary"
            onClick={() => navigate("/superadmin/alerts/create")}
          >
            Create Alert
          </ChantButton>
        }
      />

      <Table
        columns={columns}
        dataSource={alerts}
        bordered
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Preview Modal */}
      <Modal
        title={selectedAlert?.title}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <ChantButton key="close" onClick={() => setIsModalOpen(false)}>
            Close
          </ChantButton>,
        ]}
        width={560}
      >
        {selectedAlert?.imageUrl && (
          <img
            src={selectedAlert.imageUrl}
            alt={selectedAlert.title}
            style={{
              width: "100%",
              maxHeight: 240,
              objectFit: "cover",
              borderRadius: 8,
              marginBottom: 16,
            }}
          />
        )}

        <p style={{ color: "#4a5568", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>
          {selectedAlert?.description}
        </p>

        {selectedAlert?.ctaUrl && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid #edf2f7",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <LinkOutlined style={{ color: "#718096" }} />
            <span style={{ color: "#718096", fontSize: 13 }}>
              CTA navigates to: <strong>{selectedAlert.ctaUrl}</strong>
            </span>
          </div>
        )}
      </Modal>
    </SuperAdminContainer>
  );
};

export default Alerts;
